/**
 * B1088 — Tableau de latence (Eric 2026-09-05 SPEC STRICK PROD).
 *
 * Deux questions, deux tableaux :
 *   1. où part le temps du serveur aujourd'hui (tri par temps CUMULÉ : une
 *      route lente appelée deux fois ne coûte rien, une route correcte
 *      appelée mille fois, si) ;
 *   2. quelle page de l'app déclenche le plus d'appels par affichage — c'est
 *      là que se cache le vrai gaspillage côté navigateur.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Gauge, ArrowClockwise } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

const ms = (v) => (v === null || v === undefined ? "—" : `${Math.round(v)} ms`);
const secs = (v) => (v === null || v === undefined ? "—" : `${(v / 1000).toFixed(1)} s`);

const CallsBadge = ({ value }) => {
  if (value === null || value === undefined) return <span className="text-zinc-400">—</span>;
  const tone = value >= 15
    ? "bg-red-50 text-red-700"
    : value >= 8
      ? "bg-amber-50 text-amber-700"
      : "bg-emerald-50 text-emerald-700";
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${tone}`}>
      {value}
    </span>
  );
};

export const RouteLatencyPanel = ({ initial , awaiting }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const res = await axios.get(`${API}/api/health-monitor/route-latency?top=10`, {
        withCredentials: true,
      });
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setBusy(false);
    }
  }, []);

    // Tâche 1249 — Si la page a déjà tout reçu dans l'appel groupé, la carte
  // ne refait PAS son propre appel : la page passait 23 appels par affichage.
  useEffect(() => {
    if (initial) {
      setData(initial);
      return;
    }
    // Tâche 1249 — L'appel groupé est en route : ne pas en lancer un second.
    if (awaiting) return;
    load();
  }, [load, initial, awaiting]);

  if (!data) return null;
  const slowest = data.slowest || [];
  const busiest = data.busiest || [];
  const pages = data.pages || [];

  return (
    <section
      className="rounded-xl border border-zinc-200 bg-white p-5 mb-6"
      data-testid="route-latency-panel"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-zinc-900 flex items-center gap-2">
            <Gauge size={18} />
            Tableau de latence — {data.day}
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5 max-w-2xl">
            {data.total_calls.toLocaleString("fr-CA")} appels mesurés aujourd'hui.
            Classement par temps cumulé : c'est là que part réellement le temps
            du serveur. Conservation {data.retention_days} jours.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={busy}
          className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-zinc-300 px-2.5 py-1.5 text-xs font-semibold text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
          data-testid="route-latency-refresh"
        >
          <ArrowClockwise size={13} weight="bold" /> Actualiser
        </button>
      </div>

      <h3 className="text-sm font-semibold text-zinc-800 mt-5 mb-1">
        Les 10 routes les plus coûteuses
      </h3>
      {!slowest.length ? (
        <p className="text-sm text-zinc-500" data-testid="route-latency-empty">
          Aucune mesure encore enregistrée aujourd'hui (les compteurs sont
          écrits toutes les {data.flush_interval_seconds} secondes).
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="route-latency-slowest-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-zinc-500 border-b border-zinc-200">
                <th className="py-2 pr-3">Route</th>
                <th className="py-2 pr-3 text-right">Appels</th>
                <th className="py-2 pr-3 text-right">Moyenne</th>
                <th className="py-2 pr-3 text-right">Pire cas</th>
                <th className="py-2 text-right">Temps cumulé</th>
              </tr>
            </thead>
            <tbody>
              {slowest.map((r) => (
                <tr
                  key={r.path}
                  className="border-b border-zinc-100 last:border-0"
                  data-testid={`route-latency-row-${r.path}`}
                >
                  <td className="py-2 pr-3 font-mono text-xs text-zinc-700 truncate max-w-[280px]">
                    {r.path}
                  </td>
                  <td className="py-2 pr-3 text-right text-zinc-600">{r.count}</td>
                  <td className="py-2 pr-3 text-right font-mono text-zinc-600">{ms(r.avg_ms)}</td>
                  <td className="py-2 pr-3 text-right font-mono text-zinc-500">{ms(r.max_ms)}</td>
                  <td className="py-2 text-right font-mono font-semibold text-zinc-800">
                    {secs(r.total_ms)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3 className="text-sm font-semibold text-zinc-800 mt-6 mb-1">
        Les 10 routes les plus appelées
      </h3>
      {!busiest.length ? (
        <p className="text-sm text-zinc-500">—</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="route-latency-busiest-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-zinc-500 border-b border-zinc-200">
                <th className="py-2 pr-3">Route</th>
                <th className="py-2 pr-3 text-right">Appels</th>
                <th className="py-2 pr-3 text-right">Moyenne</th>
                <th className="py-2 text-right">Temps cumulé</th>
              </tr>
            </thead>
            <tbody>
              {busiest.map((r) => (
                <tr
                  key={r.path}
                  className="border-b border-zinc-100 last:border-0"
                  data-testid={`route-busiest-row-${r.path}`}
                >
                  <td className="py-2 pr-3 font-mono text-xs text-zinc-700 truncate max-w-[280px]">
                    {r.path}
                  </td>
                  <td className="py-2 pr-3 text-right font-semibold text-zinc-800">{r.count}</td>
                  <td className="py-2 pr-3 text-right font-mono text-zinc-600">{ms(r.avg_ms)}</td>
                  <td className="py-2 text-right font-mono text-zinc-600">{secs(r.total_ms)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3 className="text-sm font-semibold text-zinc-800 mt-6 mb-1">
        Appels par affichage de page
      </h3>
      <p className="text-xs text-zinc-500 mb-1 max-w-2xl">
        Deux mesures distinctes : les appels des 5 premières secondes après une
        navigation (le vrai coût d'ouverture, au-delà d'une quinzaine la page
        paie plusieurs fois le temps d'accès à la base pour rien) et les sondes
        de fond d'un onglet resté ouvert, ramenées à l'heure.
      </p>
      {!pages.length ? (
        <p className="text-sm text-zinc-500" data-testid="route-latency-pages-empty">
          Aucune page mesurée aujourd'hui.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="route-latency-pages-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-zinc-500 border-b border-zinc-200">
                <th className="py-2 pr-3">Page</th>
                <th className="py-2 pr-3 text-right">Affichages</th>
                <th className="py-2 pr-3 text-right">À l'ouverture</th>
                <th className="py-2 pr-3 text-right">Par affichage</th>
                <th className="py-2 text-right">En veille / h</th>
              </tr>
            </thead>
            <tbody>
              {pages.map((p) => (
                <tr
                  key={p.page}
                  className="border-b border-zinc-100 last:border-0"
                  data-testid={`route-page-row-${p.page}`}
                >
                  <td className="py-2 pr-3 font-mono text-xs text-zinc-700 truncate max-w-[280px]">
                    {p.page}
                  </td>
                  <td className="py-2 pr-3 text-right text-zinc-500">{p.loads}</td>
                  <td className="py-2 pr-3 text-right text-zinc-600">{p.calls}</td>
                  <td className="py-2 pr-3 text-right">
                    <CallsBadge value={p.calls_per_load} />
                  </td>
                  <td
                    className="py-2 text-right font-mono text-zinc-600"
                    data-testid={`route-page-idle-${p.page}`}
                  >
                    {p.idle_per_hour ?? 0}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default RouteLatencyPanel;
